(() => {
  'use strict';

  const VERSION = '2.1.0';
  const ROOT_SELECTOR = '[data-rd-overlays]';
  const STORAGE_KEYS = {
    dismissed: 'rdNewsletterDismissedAt',
    subscribed: 'rdNewsletterSubscribedAt',
    seenSession: 'rdNewsletterSeenSession'
  };
  const FOCUSABLE = [
    'a[href]',
    'button:not([disabled])',
    'input:not([disabled]):not([type="hidden"])',
    'select:not([disabled])',
    'textarea:not([disabled])',
    '[tabindex]:not([tabindex="-1"])'
  ].join(',');

  if (window.RawDelightsOverlays?.version === VERSION) {
    window.RawDelightsOverlays.init();
    return;
  }

  const parseConfig = (root) => {
    const node = root.querySelector('[data-rd-overlays-config]');
    if (!node) return null;
    try {
      return JSON.parse(node.textContent);
    } catch (error) {
      if (new URLSearchParams(location.search).get('rd_overlay_debug') === '1') {
        console.warn('[Raw Delights overlays] Invalid configuration.', error);
      }
      return null;
    }
  };

  const storage = {
    get(area, key) {
      try {
        return window[area].getItem(key);
      } catch (_error) {
        return null;
      }
    },
    set(area, key, value) {
      try {
        window[area].setItem(key, value);
        return true;
      } catch (_error) {
        return false;
      }
    }
  };

  const isTextControl = (element) => Boolean(
    element instanceof HTMLElement &&
    element.matches('input:not([type="button"]):not([type="submit"]):not([type="checkbox"]):not([type="radio"]), textarea, select, [contenteditable="true"]')
  );

  const isVisiblyOpen = (element) => {
    if (!(element instanceof HTMLElement) || element.hidden || element.getAttribute('aria-hidden') === 'true') return false;
    const style = window.getComputedStyle(element);
    if (style.display === 'none' || style.visibility === 'hidden' || Number(style.opacity) === 0) return false;
    const rect = element.getBoundingClientRect();
    return rect.width > 1 && rect.height > 1;
  };

  class OverlayInstance {
    constructor(root, config) {
      this.root = root;
      this.config = config;
      this.actions = root.querySelector('[data-rd-overlay-actions]');
      this.mobileShopCta = root.querySelector('[data-rd-mobile-primary-cta]');
      this.modal = root.querySelector('[data-rd-email-modal]');
      this.dialog = root.querySelector('[data-rd-modal-dialog]');
      this.debugEnabled = config.designMode || new URLSearchParams(location.search).get('rd_overlay_debug') === '1';
      this.cleanup = [];
      this.scrollFrame = 0;
      this.collisionFrame = 0;
      this.mobileActionPlacementFrame = 0;
      this.mobilePrimaryCtaObserver = null;
      this.modalTimer = 0;
      this.retryTimer = 0;
      this.retries = 0;
      this.engaged = false;
      this.pageViewShown = false;
      this.forceActions = false;
      this.modalStatus = 'inactive';
      this.previousFocus = null;
      this.bodyStyle = null;
      this.htmlStyle = null;
      this.debugPanel = null;
      this.boundScroll = (event) => this.onScroll(event);
      this.boundEngagement = (event) => this.onEngagement(event);
      this.boundKeydown = (event) => this.onKeydown(event);
      this.boundFocus = () => this.scheduleCollisionCheck();
      this.boundPawStatus = () => this.refreshDebug();
    }

    init() {
      if (this.root.dataset.rdOverlaysInitialized === 'true') return this;
      this.root.dataset.rdOverlaysInitialized = 'true';
      if (this.actions || this.mobileShopCta || this.modal) this.listen(window, 'scroll', this.boundScroll, { passive: true });
      this.initActions();
      this.initMobileShopCta();
      this.initModal();
      this.initCollisionObserver();
      this.initDebug();
      return this;
    }

    listen(target, type, handler, options) {
      target.addEventListener(type, handler, options);
      this.cleanup.push(() => target.removeEventListener(type, handler, options));
    }

    initActions() {
      if (!this.actions) return;
      this.actions.classList.add('is-enhanced');
      document.body.classList.add('rd-ov-actions-visible');
      this.initMobileActionPlacement();
      this.updateActionThreshold();
    }

    initMobileShopCta() {
      if (!this.mobileShopCta) return;
      this.mobileShopCta.classList.add('is-enhanced');
      this.updateMobileShopCtaVisibility();
      this.listen(window, 'resize', () => this.updateMobileShopCtaVisibility(), { passive: true });
      this.listen(window, 'orientationchange', () => this.updateMobileShopCtaVisibility(), { passive: true });
    }

    initMobileActionPlacement() {
      if (!this.actions) return;

      const schedulePlacement = () => this.scheduleMobileActionPlacement();
      const primaryCta = document.querySelector('[data-rd-mobile-primary-cta]');

      // The overlay's mobile shop action supplies this hook. A theme-level
      // replacement CTA can use the same hook and will retain the clearance.
      if (primaryCta && 'ResizeObserver' in window) {
        this.mobilePrimaryCtaObserver = new ResizeObserver(schedulePlacement);
        this.mobilePrimaryCtaObserver.observe(primaryCta);
        this.cleanup.push(() => this.mobilePrimaryCtaObserver?.disconnect());
      }

      this.listen(window, 'resize', schedulePlacement, { passive: true });
      this.listen(window, 'orientationchange', schedulePlacement, { passive: true });
      window.visualViewport?.addEventListener('resize', schedulePlacement, { passive: true });
      this.cleanup.push(() => window.visualViewport?.removeEventListener('resize', schedulePlacement));
      this.scheduleMobileActionPlacement();
    }

    scheduleMobileActionPlacement() {
      if (!this.actions || this.mobileActionPlacementFrame) return;
      this.mobileActionPlacementFrame = window.requestAnimationFrame(() => {
        this.mobileActionPlacementFrame = 0;
        const isMobile = window.matchMedia('(max-width: 748px)').matches;

        if (!isMobile) {
          if (this.actions.style.getPropertyValue('--rd-ov-mobile-action-bottom')) {
            this.actions.style.removeProperty('--rd-ov-mobile-action-bottom');
          }
          return;
        }

        const primaryCta = document.querySelector('[data-rd-mobile-primary-cta]');
        if (!(primaryCta instanceof HTMLElement) || !isVisiblyOpen(primaryCta)) {
          if (this.actions.style.getPropertyValue('--rd-ov-mobile-action-bottom')) {
            this.actions.style.removeProperty('--rd-ov-mobile-action-bottom');
          }
          return;
        }

        const rect = primaryCta.getBoundingClientRect();
        const viewportHeight = window.visualViewport?.height || window.innerHeight;
        const gap = 12;
        const clearance = Math.max(0, viewportHeight - rect.top + gap);
        const clearanceValue = `${Math.ceil(clearance)}px`;
        if (this.actions.style.getPropertyValue('--rd-ov-mobile-action-bottom') !== clearanceValue) {
          this.actions.style.setProperty('--rd-ov-mobile-action-bottom', clearanceValue);
        }
      });
    }

    onScroll(event) {
      this.onEngagement(event);
      if ((!this.actions && !this.mobileShopCta) || this.scrollFrame) return;
      this.scrollFrame = window.requestAnimationFrame(() => {
        this.scrollFrame = 0;
        this.updateActionThreshold();
        this.updateMobileShopCtaVisibility();
      });
    }

    updateActionThreshold() {
      if (!this.actions) return;
      const before = window.scrollY < Number(this.config.actions?.revealAfter || 0);
      this.actions.classList.toggle('is-before-threshold', before);
      this.actions.classList.toggle('is-visible', !before);
      this.refreshDebug();
    }

    updateMobileShopCtaVisibility() {
      if (!this.mobileShopCta) return;
      const isMobile = window.matchMedia('(max-width: 748px)').matches;
      const hero = document.querySelector('[data-rd-hero], .banner, [class*="hero"], .slideshow');
      const pastHero = hero instanceof HTMLElement
        ? hero.getBoundingClientRect().bottom <= 0
        : window.scrollY >= Number(this.config.actions?.revealAfter || 0);
      this.mobileShopCta.classList.toggle('is-visible', isMobile && pastHero);
    }

    initCollisionObserver() {
      if (!this.actions) return;
      this.listen(document, 'focusin', this.boundFocus, true);
      this.listen(document, 'focusout', this.boundFocus, true);
      this.listen(document, 'click', this.boundFocus, true);

      if ('MutationObserver' in window && document.body) {
        const observer = new MutationObserver((mutations) => {
          const diagnosticsOnly = mutations.every((mutation) => mutation.target instanceof Element && mutation.target.closest('.rd-ov-debug'));
          if (!diagnosticsOnly) {
            this.scheduleCollisionCheck();
            this.scheduleMobileActionPlacement();
          }
        });
        observer.observe(document.body, {
          subtree: true,
          childList: true,
          attributes: true,
          attributeFilter: ['aria-hidden', 'aria-modal', 'class', 'open', 'style']
        });
        this.cleanup.push(() => observer.disconnect());
      }
      this.scheduleCollisionCheck();
    }

    scheduleCollisionCheck() {
      if (!this.actions || this.collisionFrame) return;
      this.collisionFrame = window.requestAnimationFrame(() => {
        this.collisionFrame = 0;
        this.updateActionCollision();
      });
    }

    findOtherDialog(respectActionSetting = true) {
      if (respectActionSetting && !this.config.actions?.hideForDialog) return null;
      const selectors = [
        'dialog[open]',
        '[role="dialog"][aria-modal="true"]',
        '[aria-modal="true"]',
        '.quick-add-modal[open]',
        '.quick-add-modal.active',
        '.quick-add-modal.is-open',
        'cart-drawer[open]',
        '.cart-drawer.is-open'
      ].join(',');
      const candidates = document.querySelectorAll(selectors);
      for (const candidate of candidates) {
        if (candidate === this.dialog || this.modal?.contains(candidate)) continue;
        if (isVisiblyOpen(candidate)) return candidate;
      }
      return null;
    }

    updateActionCollision() {
      if (!this.actions) return;
      const mobileFieldFocused = window.matchMedia('(max-width: 748px)').matches && isTextControl(document.activeElement);
      const modalOpen = Boolean(this.modal?.classList.contains('is-open'));
      const suspended = !this.forceActions && (mobileFieldFocused || modalOpen || Boolean(this.findOtherDialog()));
      this.actions.classList.toggle('is-suspended', suspended);
      this.actions.classList.toggle('is-force-visible', this.forceActions);
      this.refreshDebug();
    }

    initModal() {
      if (!this.modal || !this.dialog || !this.config.modal?.enabled) {
        this.modalStatus = 'disabled';
        return;
      }

      const close = this.root.querySelector('[data-rd-modal-close]');
      const backdrop = this.root.querySelector('[data-rd-modal-backdrop]');
      const preview = this.root.querySelector('[data-rd-preview-modal]');
      const formState = this.root.querySelector('[data-rd-form-state]');

      if (close) this.listen(close, 'click', () => this.closeModal({ persistDismissal: !this.config.designMode }));
      if (backdrop) this.listen(backdrop, 'click', () => {
        if (this.config.modal.closeOnBackdrop) this.closeModal({ persistDismissal: !this.config.designMode });
      });
      if (preview) this.listen(preview, 'click', () => this.openModal({ preview: true, force: true }));
      this.listen(document, 'keydown', this.boundKeydown, true);
      this.listen(window, 'wheel', this.boundEngagement, { passive: true });
      this.listen(window, 'touchmove', this.boundEngagement, { passive: true });
      this.listen(document, 'visibilitychange', () => this.refreshDebug());

      if (formState?.dataset.success === 'true') {
        storage.set('localStorage', STORAGE_KEYS.subscribed, String(Date.now()));
        this.modalStatus = 'subscribed';
        window.requestAnimationFrame(() => this.openModal({ force: true }));
        return;
      }
      if (formState?.dataset.errors === 'true') {
        this.modalStatus = 'form-error';
        window.requestAnimationFrame(() => this.openModal({ force: true }));
        return;
      }

      if (this.config.designMode) {
        this.modalStatus = 'design-mode';
      } else if (this.shouldSuppressAutoModal()) {
        this.modalStatus = 'suppressed';
      } else {
        this.modalStatus = 'awaiting-engagement';
      }
    }

    shouldSuppressAutoModal() {
      if (!this.config.modal.eligible || this.config.modal.customerSubscribed || this.pageViewShown) return true;
      if (storage.get('sessionStorage', STORAGE_KEYS.seenSession)) return true;
      const now = Date.now();
      const dismissedAt = Number(storage.get('localStorage', STORAGE_KEYS.dismissed) || 0);
      const subscribedAt = Number(storage.get('localStorage', STORAGE_KEYS.subscribed) || 0);
      const dismissalWindow = Number(this.config.modal.dismissalDays || 14) * 86400000;
      if (dismissedAt && now - dismissedAt < dismissalWindow) return true;
      if (subscribedAt && now - subscribedAt < 365 * 86400000) return true;
      return false;
    }

    onEngagement(event) {
      if (!event.isTrusted || this.engaged || this.modalStatus !== 'awaiting-engagement') return;
      this.engaged = true;
      this.modalStatus = 'waiting';
      const delay = Math.max(0, Number(this.config.modal.delaySeconds || 10) * 1000);
      this.modalTimer = window.setTimeout(() => {
        this.modalTimer = 0;
        this.tryAutomaticOpen();
      }, delay);
      this.refreshDebug();
    }

    tryAutomaticOpen() {
      if (this.shouldSuppressAutoModal()) {
        this.modalStatus = 'suppressed';
        this.refreshDebug();
        return;
      }

      const blocked = document.visibilityState !== 'visible' || isTextControl(document.activeElement) || Boolean(this.findOtherDialog(false));
      if (!blocked) {
        this.openModal();
        return;
      }

      if (this.retries >= 5) {
        this.modalStatus = 'abandoned';
        this.refreshDebug();
        return;
      }
      this.retries += 1;
      this.modalStatus = `retry-${this.retries}`;
      this.retryTimer = window.setTimeout(() => {
        this.retryTimer = 0;
        this.tryAutomaticOpen();
      }, 3000);
      this.refreshDebug();
    }

    onKeydown(event) {
      if (this.modal?.classList.contains('is-open')) {
        if (event.key === 'Escape') {
          event.preventDefault();
          this.closeModal({ persistDismissal: !this.config.designMode });
          return;
        }
        if (event.key === 'Tab') this.trapFocus(event);
        return;
      }
      this.onEngagement(event);
    }

    trapFocus(event) {
      const focusable = Array.from(this.dialog.querySelectorAll(FOCUSABLE)).filter(isVisiblyOpen);
      if (!focusable.length) {
        event.preventDefault();
        this.dialog.focus({ preventScroll: true });
        return;
      }
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    }

    lockScroll() {
      this.bodyStyle = {
        overflow: document.body.style.overflow,
        paddingRight: document.body.style.paddingRight
      };
      this.htmlStyle = { overflow: document.documentElement.style.overflow };
      const scrollbarWidth = Math.max(0, window.innerWidth - document.documentElement.clientWidth);
      if (scrollbarWidth) document.body.style.paddingRight = `${scrollbarWidth}px`;
      document.body.style.overflow = 'hidden';
      document.documentElement.style.overflow = 'hidden';
      document.body.classList.add('rd-ov-modal-open');
    }

    unlockScroll() {
      if (!this.bodyStyle) return;
      Object.assign(document.body.style, this.bodyStyle);
      if (this.htmlStyle) Object.assign(document.documentElement.style, this.htmlStyle);
      this.bodyStyle = null;
      this.htmlStyle = null;
      document.body.classList.remove('rd-ov-modal-open');
    }

    openModal({ preview = false, force = false } = {}) {
      if (!this.modal || !this.dialog || this.modal.classList.contains('is-open')) return false;
      if (!force && (this.shouldSuppressAutoModal() || document.visibilityState !== 'visible' || isTextControl(document.activeElement) || this.findOtherDialog(false))) return false;

      this.previousFocus = document.activeElement instanceof HTMLElement ? document.activeElement : null;
      if (!preview) this.pageViewShown = true;
      if (!preview) storage.set('sessionStorage', STORAGE_KEYS.seenSession, '1');
      this.modal.dataset.preview = preview ? 'true' : 'false';
      this.modal.hidden = false;
      this.modal.setAttribute('aria-hidden', 'false');
      this.lockScroll();
      window.requestAnimationFrame(() => {
        this.modal.classList.add('is-open');
        const target = this.modal.querySelector('[data-rd-form-errors]') || this.modal.querySelector('input[type="email"]') || this.modal.querySelector('[data-rd-form-success]') || this.dialog;
        target.focus?.({ preventScroll: true });
      });
      this.modalStatus = preview ? 'preview-open' : 'open';
      this.scheduleCollisionCheck();
      this.refreshDebug();
      return true;
    }

    closeModal({ persistDismissal = false, restoreFocus = true } = {}) {
      if (!this.modal || !this.modal.classList.contains('is-open')) return;
      if (persistDismissal && this.modal.dataset.preview !== 'true') {
        storage.set('localStorage', STORAGE_KEYS.dismissed, String(Date.now()));
      }
      this.modal.classList.remove('is-open');
      this.modal.setAttribute('aria-hidden', 'true');
      this.modal.hidden = true;
      this.modal.dataset.preview = 'false';
      this.unlockScroll();
      this.modalStatus = 'closed';
      this.scheduleCollisionCheck();
      if (restoreFocus && this.previousFocus?.isConnected) this.previousFocus.focus({ preventScroll: true });
      this.previousFocus = null;
      this.refreshDebug();
    }

    initDebug() {
      if (!this.debugEnabled) return;
      const panel = document.createElement('aside');
      panel.className = 'rd-ov-debug';
      panel.setAttribute('aria-label', 'Raw Delights overlay diagnostics');
      panel.innerHTML = '<dl data-rd-debug-values></dl><div class="rd-ov-debug__actions"><button type="button" data-rd-debug-actions>Force show sticky actions</button><button type="button" data-rd-debug-modal>Preview email modal</button><button type="button" data-rd-debug-paws>Rebuild paw trail</button></div>';
      document.body.appendChild(panel);
      this.debugPanel = panel;
      this.listen(panel.querySelector('[data-rd-debug-actions]'), 'click', () => {
        this.forceActions = true;
        this.updateActionCollision();
      });
      this.listen(panel.querySelector('[data-rd-debug-modal]'), 'click', () => this.openModal({ preview: true, force: true }));
      this.listen(panel.querySelector('[data-rd-debug-paws]'), 'click', () => window.RawDelightsPawTrail?.rebuild());
      this.listen(document, 'rd:paw-status', this.boundPawStatus);
      this.refreshDebug();
    }

    refreshDebug() {
      if (!this.debugPanel) return;
      const values = this.debugPanel.querySelector('[data-rd-debug-values]');
      const roots = document.querySelectorAll(ROOT_SELECTOR);
      const rect = this.actions?.getBoundingClientRect();
      const style = this.actions ? window.getComputedStyle(this.actions) : null;
      const paw = window.RawDelightsPawTrail?.getStatus?.() || {};
      const entries = [
        ['Overlay section found', roots.length ? 'yes' : 'no'],
        ['Overlay instances', String(roots.length)],
        ['Call enabled', this.config.callEnabled ? 'yes' : 'no'],
        ['Resolved phone number', this.config.phoneSource === 'missing' ? 'missing' : 'configured'],
        ['Location enabled', this.config.locationEnabled ? 'yes' : 'no'],
        ['Resolved directions URL', this.config.hasDirectionsUrl ? 'yes' : 'no'],
        ['Action dimensions', rect ? `${Math.round(rect.width)}×${Math.round(rect.height)}` : 'missing'],
        ['Action display', style?.display || 'missing'],
        ['Action opacity', style?.opacity || 'missing'],
        ['Action z-index', style?.zIndex || 'missing'],
        ['Modal status', this.modalStatus],
        ['Paw trail status', paw.status || 'inactive'],
        ['Paw count', String(paw.pawCount || 0)],
        ['Trail build duration', `${Math.round(paw.buildDuration || 0)}ms`],
        ['Trail rebuild count', String(paw.rebuildCount || 0)],
        ['Main selector used', paw.mainSelector || 'not found']
      ];
      values.innerHTML = entries.map(([term, value]) => `<dt>${term}</dt><dd>${value}</dd>`).join('');
    }

    destroy() {
      window.clearTimeout(this.modalTimer);
      window.clearTimeout(this.retryTimer);
      if (this.scrollFrame) window.cancelAnimationFrame(this.scrollFrame);
      if (this.collisionFrame) window.cancelAnimationFrame(this.collisionFrame);
      if (this.mobileActionPlacementFrame) window.cancelAnimationFrame(this.mobileActionPlacementFrame);
      if (this.modal?.classList.contains('is-open')) this.closeModal({ restoreFocus: false });
      this.cleanup.splice(0).forEach((remove) => remove());
      this.actions?.classList.remove('is-enhanced', 'is-before-threshold', 'is-visible', 'is-suspended', 'is-force-visible');
      this.mobileShopCta?.classList.remove('is-enhanced', 'is-visible');
      this.debugPanel?.remove();
      this.debugPanel = null;
      this.root.removeAttribute('data-rd-overlays-initialized');
      if (!document.querySelector(`${ROOT_SELECTOR}[data-rd-overlays-initialized="true"] [data-rd-overlay-actions]`)) {
        document.body.classList.remove('rd-ov-actions-visible');
      }
      document.body.classList.remove('rd-ov-modal-open');
    }
  }

  const instances = new Map();

  const manager = {
    version: VERSION,
    instances,
    init(scope = document) {
      const roots = [];
      if (scope instanceof Element && scope.matches(ROOT_SELECTOR)) roots.push(scope);
      scope.querySelectorAll?.(ROOT_SELECTOR).forEach((root) => roots.push(root));
      roots.forEach((root) => {
        if (instances.has(root)) return;
        const config = parseConfig(root);
        if (!config?.enabled) return;
        const instance = new OverlayInstance(root, config).init();
        instances.set(root, instance);
      });
      return manager;
    },
    destroy(scope) {
      for (const [root, instance] of instances) {
        if (!scope || root === scope || (scope instanceof Element && scope.contains(root))) {
          instance.destroy();
          instances.delete(root);
        }
      }
      return manager;
    },
    preview() {
      instances.values().next().value?.openModal({ preview: true, force: true });
    }
  };

  window.RawDelightsOverlays = manager;

  const start = () => manager.init(document);
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start, { once: true });
  else start();

  if (!window.__rdOverlaySectionListeners) {
    window.__rdOverlaySectionListeners = true;
    document.addEventListener('shopify:section:load', (event) => manager.init(event.target));
    document.addEventListener('shopify:section:unload', (event) => manager.destroy(event.target));
    document.addEventListener('shopify:section:reorder', () => manager.init(document));
  }
})();
